import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Menu, Button, Tooltip, Dropdown, Modal } from "antd";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  UserOutlined,
  EditOutlined,
  BookOutlined,
  SnippetsOutlined,
  LogoutOutlined,
  BarChartOutlined,
  FileOutlined,
  TeamOutlined,
  CompassOutlined,
  WalletOutlined,
  BankOutlined,
  AccountBookOutlined,
  PhoneOutlined,
  CarOutlined,
  AppstoreAddOutlined,
} from "@ant-design/icons";
import styled, { css } from "styled-components";
import { useDispatch } from "react-redux";
import { Logout } from "src/redux/actions/authAction";

const { Content } = Layout;

const StyledLayout = styled(Layout)`
  min-height: 100vh;
  display: flex;
`;

const Sidebar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "collapsed",
})<{ collapsed: boolean }>`
  width: ${({ collapsed }) => (collapsed ? "60px" : "170px")};
  transition: width 0.3s ease-in-out;
  background: #001529;
  color: white;
  overflow: hidden;
  position: fixed;
  height: 100vh;
  top: 0;
  left: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .ant-menu {
    background: #001529;
    color: white;
  }

  .ant-menu-item,
  .ant-menu-submenu-title {
    color: white !important;
  }
  // .ant-menu-submenu .ant-menu-item {
  //   background-color: #693b88ff;
  // }

  ${({ collapsed }) =>
    collapsed &&
    css`
      .ant-menu-submenu-arrow {
        display: none !important;
      }
    `}

  .ant-menu-item {
    position: relative;
    ${({ collapsed }) =>
      collapsed &&
      css`
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        padding: 0 !important;
        height: 50px;
        text-align: center;

        .ant-menu-title-content {
          display: none;
        }

        &::after {
          content: attr(title);
          position: absolute;
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(0, 0, 0, 0.85);
          color: white !important;
          padding: 4px 10px;
          font-size: 12px;
          white-space: nowrap;
          border-radius: 4px;
          margin-left: 10px;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.2s ease;
          z-index: 9999;
        }

        &:hover::after {
          opacity: 1;
        }
      `}
  }

  .ant-menu-item-selected {
    color: white !important;
    font-weight: bold;
    padding-left: ${({ collapsed }) => (collapsed ? "0px" : "28px")} !important;
    transition: all 0.3s ease;
  }

  .ant-menu-item:hover {
    background-color: #ffffff71 !important;
    color: #000000c9 !important;
    font-weight: bold;

    .ant-menu-item-icon {
      color: rgba(93, 35, 107, 0.89) !important;
    }
  }

  .ant-menu-item.ant-menu-item-selected {
    background-color: #fff !important;
    border-left: 4px solid #fff !important;
    color: rgba(93, 35, 107, 0.89) !important;
    font-weight: bold;
  }
  li.ant-menu-item {
    padding-left: 2px !important;
  }

  .ant-menu-submenu-title {
   padding-left: ${({ collapsed }) => (collapsed ? "20px" : "2px")} !important;
  }

  /* Indent child items and show a vertical line */

  .ant-menu-submenu-title {
    background-color: transparent !important;
    border-radius: 0px;
    font-weight: 500;
    color: white !important;
  }

  .ant-menu-submenu-open > .ant-menu-submenu-title {
    background-color: rgba(255, 0, 0, 0.19) !important;
  }

  .ant-menu-item-selected {
    background-color: rgba(255, 255, 255, 0.25) !important;
    color: #884299 !important;
  }

  .ant-menu-sub.ant-menu-inline {
    margin-left: 16px;
    border-left: 2px solid rgba(255, 255, 255, 0.2);
    padding-left: 2px;
  }

  .ant-menu-submenu-arrow {
    color: white !important;
  }

  .ant-menu-submenu .ant-menu-item-selected {
    border-radius: 0px;
    background-color: #884299 !important;
    color: #ffffffff !important;
    padding-right: 0px !important;
  }
`;

// const UserInfo = styled.div<{ collapsed: boolean }>`
//   display: flex;
//   align-items: center;
//   justify-content: ${({ collapsed }) => (collapsed ? "center" : "flex-start")};
//   padding: 10px;
//   color: white;
//   margin-bottom: 20px;
// `;

interface OpenedTabs {
  [key: string]: boolean;
}

interface MenuItem {
  label: string;
  key: string;
  route?: string;
  icon: React.ReactNode;
  newTab?: boolean;
  children?: MenuItem[];
}

const SidebarMenu: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [openedTabs, setOpenedTabs] = useState<OpenedTabs>(() =>
    JSON.parse(localStorage.getItem("openedTabs") || "{}")
  );

  const navigate = useNavigate();
  const location = useLocation();
  const openedTabRefs = useRef<Record<string, Window | null>>({});
  const dispatch = useDispatch();
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");

  const menuItems: MenuItem[] = [
    {
      label: "Dashboard",
      key: "home",
      route: "/dashboard/home",
      icon: <UserOutlined />,
      newTab: true,
    },
    {
      label: "Vehicle Account",
      key: "vehicleaccount",
      route: "/dashboard/vehicleaccount",
      icon: <CarOutlined />,
      newTab: true,
    },
    {
      label: "Exit Vehicle",
      key: "exidevehicle",
      route: "/dashboard/exidevehicle",
      icon: <UserOutlined />,
      newTab: true,
    },
    {
      label: "Payment Report",
      key: "paymentreport",
      route: "/dashboard/paymentreport",
      icon: <FileOutlined />,
      newTab: true,
    },
    // {
    //   label: "Driver Attendance",
    //   key: "driverattendance",
    //   route: "/dashboard/driverattendance",
    //   icon: <TeamOutlined />,
    //   newTab: true,
    // },
    {
      label: "Enterprises",
      key: "enterprises",
      route: "/dashboard/enterprises",
      icon: <AppstoreAddOutlined />,
      newTab: true,
    },
    {
      label: "Vehicle Master",
      key: "vehiclemaster",
      route: "/dashboard/vehiclemaster",
      icon: <CarOutlined />,
      newTab: true,
    },
    {
      label: "Driver Calls",
      key: "drivercalls",
      route: "/dashboard/drivercalls",
      icon: <CarOutlined />,
      newTab: true,
    },
    {
      label: "Report",
      key: "report",
      icon: <CompassOutlined />,
      children: [
        {
          label: "Corporate Trip",
          key: "corporatetrip",
          route: "/dashboard/corporatetrip",
          icon: <CompassOutlined />,
          newTab: true,
        },
        {
          label: "Prepaid Trip",
          key: "prepaidtrip",
          route: "/dashboard/prepaidtrip",
          icon: <WalletOutlined />,
          newTab: true,
        },
        {
          label: "Booking Report",
          key: "booking_page",
          route: "/dashboard/booking_page",
          icon: <TeamOutlined />,
          newTab: true,
        },
        {
          label: "Owner Details",
          key: "ownerdetails",
          route: "/dashboard/ownerdetails",
          icon: <WalletOutlined />,
          newTab: true,
        },
        {
          label: "Driver Details",
          key: "driverdetails",
          route: "/dashboard/driverdetails",
          icon: <TeamOutlined />,
          newTab: true,
        },
        {
          label: "Active / Inactive",
          key: "statusreport",
          route: "/dashboard/statusreport",
          icon: <TeamOutlined />,
          newTab: true,
        },
      ],
    },
  ];

  useEffect(() => {
    localStorage.setItem("openedTabs", JSON.stringify(openedTabs));
  }, [openedTabs]);

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  const handleTabClose = (key: string) => {
    delete openedTabRefs.current[key];
    setOpenedTabs((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    localStorage.removeItem(`tab-lock-${key}`);
  };

  const forceLogout = localStorage.getItem("force-logout");

  useEffect(() => {
    console.log(forceLogout);
    const onStorage = (event: StorageEvent) => {
      if (event.key === "force-logout" && event.newValue === "true") {
        const isLeader = sessionStorage.getItem("logout-leader") === "true";

        const protectedTabs = [
          "home",
          "vehicle-list",
          "vehicle-master",
          "inspection",
        ];
        protectedTabs.forEach((tab) => {
          localStorage.removeItem(`tab-lock-${tab}`);
        });
        sessionStorage.clear();

        if (!isLeader) {
          window.close();
        } else {
          navigate("/login", { replace: true });
        }
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [forceLogout, navigate]);

  const openNewTab = useCallback((route: string, key: string) => {
    const tabWindow = window.open(`${window.location.origin}${route}`, key);

    if (tabWindow) {
      openedTabRefs.current[key] = tabWindow;
      setOpenedTabs((prev) => ({ ...prev, [key]: true }));

      localStorage.setItem(`tab-lock-${key}`, "true");

      const checkTabClosed = setInterval(() => {
        if (tabWindow.closed) {
          clearInterval(checkTabClosed);
          handleTabClose(key);
        }
      }, 1000);
    }
  }, []);

  const flattenMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items.reduce<MenuItem[]>((acc, item) => {
      acc.push(item);
      if (item.children) acc.push(...item.children);
      return acc;
    }, []);
  };

  const currentKey = flattenMenuItems(menuItems).find(
    (item) => item.route && location.pathname.startsWith(item.route)
  )?.key;

  const handleLogout = () => {
    Modal.confirm({
      title: "Confirm Logout",
      content: "Are you sure you want to logout?",
      okText: "Yes",
      cancelText: "Cancel",
      centered: true,
      onOk: () => {
        sessionStorage.setItem("logout-leader", "true");
        localStorage.setItem("force-logout", "true");

        dispatch(
          Logout({
            username: userData.employeename,
            roleid: userData.roleid,
            employeeid: userData.employeeid,
          })
        );
        window.localStorage.setItem("logout", Date.now().toString());
        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("userData");
        navigate("/", { replace: true });

        const protectedTabs = [
          "dashboard",
          "vehicleaccount",
          "history",
          "driverattendance",
          "corporatetrip",
          "prepaidtrip",
        ];
        protectedTabs.forEach((tab) => {
          localStorage.removeItem(`tab-lock-${tab}`);
        });
        sessionStorage.clear();

        setTimeout(() => {
          localStorage.removeItem("force-logout");
        }, 100);

        Object.values(openedTabRefs.current).forEach((tab) => {
          if (tab && !tab.closed) tab.close();
        });

        navigate("/login", { replace: true });
      },
    });
  };

  const userMenu = (
    <Menu>
      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <StyledLayout>
      <Sidebar
        collapsed={collapsed}
        style={{
          backgroundColor: "rgb(136, 66, 153)",
          overflow: collapsed ? "visible" : "auto",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: collapsed ? "center" : "flex-start",
            }}
          >
            <Button
              onClick={toggleCollapsed}
              style={{ margin: 16 }}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            />
            {!collapsed && (
              <div
                className="logos"
                style={{ marginTop: "12px", marginLeft: "-10px" }}
              >
                {!collapsed && (
                  <span
                    style={{
                      fontSize: "20px",
                      display: "flex",
                      margin: "5% 0% 0% 0%",
                    }}
                  >
                    Hi , {userData.employeename}
                  </span>
                )}
              </div>
            )}
          </div>

          <Menu
            mode="inline"
            selectedKeys={currentKey ? [currentKey] : []}
            style={{ backgroundColor: "rgb(136, 66, 153)", paddingLeft: "0" }}
          >
            {menuItems.map((item) => {
              if (item.children) {
                return (
                  <Menu.SubMenu
                    style={{ paddingLeft: collapsed ? 0 : 0 }}
                    key={item.key}
                    icon={
                      <Tooltip title={item.label} placement="right">
                        <span>{item.icon}</span>
                      </Tooltip>
                    }
                    title={
                      <span style={{ color: "white", fontWeight: 500 }}>
                        {collapsed ? "" : item.label}
                      </span>
                    }
                  >
                    {item.children.map((child) => (
                      <Menu.Item
                        key={child.key}
                        icon={
                          <Tooltip title={child.label} placement="right">
                            {child.icon}
                          </Tooltip>
                        }
                        onClick={() => {
                          if (child.newTab) openNewTab(child.route!, child.key);
                          else navigate(child.route!);
                        }}
                      >
                        <span style={{ color: "white" }}>{child.label}</span>
                      </Menu.Item>
                    ))}
                  </Menu.SubMenu>
                );
              } else {
                return (
                  <Menu.Item
                    key={item.key}
                    icon={
                      <Tooltip title={item.label} placement="right">
                        {item.icon}
                      </Tooltip>
                    }
                    onClick={() => {
                      if (item.newTab) openNewTab(item.route!, item.key);
                      else navigate(item.route!);
                    }}
                    title={collapsed ? item.label : ""}
                  >
                    {item.label}
                  </Menu.Item>
                );
              }
            })}
          </Menu>
        </div>
        <div
          style={{
            borderTop: "1px solid rgba(255,255,255,0.1)",
            padding: "10px 0",
          }}
        >
          <Menu
            theme="dark"
            selectable={false}
            style={{ backgroundColor: "rgba(93, 35, 107, 0.89)" }}
          >
            <Menu.Item
              key="logout"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              Logout
            </Menu.Item>
          </Menu>
        </div>
      </Sidebar>

      <Layout
        style={{
          marginLeft: collapsed ? "60px" : "165px",
          transition: "margin-left 0.3s ease",
        }}
      >
        <Content style={{ padding: "20px" }}>
          <Outlet />
        </Content>
      </Layout>
    </StyledLayout>
  );
};

export default SidebarMenu;
